// type Confirm

import { AxiosError } from "axios";
import { MouseEventHandler, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { storeAuthResult } from "utils/token.util";

import { confirmSignup } from "~/api/auth/confirmSignup";
import { loginUser } from "~/api/auth/loginUser";
import { resendConfirmationCode } from "~/api/auth/resendConfirmationCode";
import MainButton from "~/components/common/mainButton";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { CreateMemberInput } from "~/types/user/user.types";

import styles from "./index.module.scss";

type ErrorMessage = {
  message: string;
};

type Props = {
  goToNextStep: () => void;
};

const ApplicationConfirmationCode = ({ goToNextStep }: Props) => {
  const [error, setError] = useState("");

  const { getValues } = useFormContext<CreateMemberInput>();
  const codeRef = useRef<HTMLInputElement>(null);
  const { language } = useLanguage();
  const applyTexts =
    language === "En"
      ? englishTranslations.applyPage
      : frenchTranslations.applyPage;

  const queryClient = useQueryClient();

  const { mutate: loginMutation, isLoading: loginLoading } = useMutation(
    loginUser,
    {
      onSuccess: (res) => {
        storeAuthResult(res);
        queryClient.invalidateQueries(["user"]);
        goToNextStep();
      },
    }
  );

  const { mutate: confirmSignupMutation, isLoading: confirmSignupLoading } =
    useMutation(confirmSignup, {
      onSuccess: () => {
        const { username, password } = getValues();
        toast.success(applyTexts.successful.validatedMessage4, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
        loginMutation({ username, password });
      },
      onError: (err: AxiosError<ErrorMessage>) => {
        const message = err.response?.data.message;
        if (typeof message === "string") {
          setError(message);
        }
      },
    });

  const { mutate: resendCodeMutation, isLoading: resendCodeLoading } =
    useMutation(resendConfirmationCode, {
      onSuccess: () => {
        setError("");
      },
      onError: (err: AxiosError<ErrorMessage>) => {
        const message = err.response?.data.message;
        if (typeof message === "string") {
          setError(message);
        }
      },
    });

  const handleResendCodeMutation = () => {
    const username = getValues("username");
    resendCodeMutation({ username });
  };

  const handleConfirm: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();

    const username = getValues("username");
    const code = codeRef.current?.value;

    if (!code) {
      codeRef.current?.focus();
      return;
    }

    if (!username) {
      setError("Could not find username");
      return;
    }

    confirmSignupMutation({
      username,
      code,
    });
  };

  return (
    <div className={styles.container}>
      <p>{applyTexts.applicationForm.enterConfirmationCode}</p>
      <div>
        <input
          className="main-input"
          placeholder="code"
          size={7}
          ref={codeRef}
        />
        {error && <p>{error}</p>}
      </div>
      <p className={styles.resendCode}>
        {applyTexts.applicationForm.resendCodeText}{" "}
        <span onClick={handleResendCodeMutation}>
          {applyTexts.applicationForm.resendCodeButton}
        </span>
      </p>
      <MainButton
        isLoading={confirmSignupLoading || loginLoading || resendCodeLoading}
        onClick={handleConfirm}
      >
        {applyTexts.applicationForm.submit}
      </MainButton>
    </div>
  );
};

export default ApplicationConfirmationCode;
